import firebase from "firebase/app";
import {
  tAdminConnection,
  tConnectionSource,
  tConnectionStatus,
  tRepConnection,
  tRepRetailerConnection,
  tUserConnection,
  validate,
} from "tmt_api/src/types/types.validator";

export type tQueryAdminConnectionsParams = {
  id?: string;
  status?: tConnectionStatus | tConnectionStatus[];
  term?: string;
  type?: "brand" | "retailer" | "rep";
};

export type tQueryBrandRetailerConnectionsParams = {
  id?: string;
  source?: tConnectionSource;
  status?: tConnectionStatus | tConnectionStatus[];
  brandId?: string;
  retailerId?: string;
  repId?: string;
  term?: string;
  isSalesRep?: boolean;
  isTradeshowCustomer?: boolean;
};

export type tQueryBrandRepConnectionsParams = {
  id?: string;
  source?: tConnectionSource;
  status?: tConnectionStatus | tConnectionStatus[];
  brandId?: string;
  repId?: string;
  term?: string;
};

export const documentAdminConnection = (id: string): firebase.firestore.DocumentReference<tAdminConnection> =>
  firebase
    .firestore()
    .collection("admin_connections")
    .doc(id)
    .withConverter<tAdminConnection>({
      toFirestore: (data) => data,
      fromFirestore: (snapshot) => validate("tAdminConnection")(snapshot.data()),
    });

export const documentBrandRetailerConnection = (id: string): firebase.firestore.DocumentReference<tUserConnection> =>
  firebase
    .firestore()
    .collection("user_connections")
    .doc(id)
    .withConverter<tUserConnection>({
      toFirestore: (data) => data,
      fromFirestore: (snapshot) => validate("tUserConnection")(snapshot.data()),
    });

export const documentBrandRepConnection = (id: string): firebase.firestore.DocumentReference<tRepConnection> =>
  firebase
    .firestore()
    .collection("rep_connections")
    .doc(id)
    .withConverter<tRepConnection>({
      toFirestore: (data) => data,
      fromFirestore: (snapshot) => validate("tRepConnection")(snapshot.data()),
    });

export const documentRepRetailerConnection = (
  id: string,
): firebase.firestore.DocumentReference<tRepRetailerConnection> =>
  firebase
    .firestore()
    .collection("reps_retailers_connections")
    .doc(id)
    .withConverter<tRepRetailerConnection>({
      toFirestore: (data) => data,
      fromFirestore: (snapshot) => validate("tRepRetailerConnection")(snapshot.data()),
    });

export const queryAdminConnection = (
  params?: tQueryAdminConnectionsParams,
): firebase.firestore.Query<tAdminConnection> => {
  let collectionRef: firebase.firestore.Query = firebase
    .firestore()
    .collection("admin_connections")
    .orderBy("startDate", "desc");

  if (params?.id) collectionRef = collectionRef.where("uuid", "==", params.id);
  if (params?.status)
    collectionRef = collectionRef.where("status", Array.isArray(params?.status) ? "in" : "==", params.status);
  if (params?.term)
    collectionRef = collectionRef.where("searchableValues", "array-contains", params.term.toLowerCase());
  if (params?.type) collectionRef = collectionRef.where("application.type", "==", params.type);

  return collectionRef.where("type", "==", "Admin").withConverter<tAdminConnection>({
    toFirestore: (data) => data,
    fromFirestore: (snapshot) => validate("tAdminConnection")(snapshot.data()),
  });
};

export const queryBrandRetailerConnection = (
  params?: tQueryBrandRetailerConnectionsParams,
): firebase.firestore.Query<tUserConnection> => {
  let collectionRef: firebase.firestore.Query = firebase
    .firestore()
    .collection("user_connections")
    .orderBy("startDate", "desc");

  if (params?.id) collectionRef = collectionRef.where("uuid", "==", params.id);
  if (params?.source) collectionRef = collectionRef.where("connectionSource", "==", params.source);
  if (params?.status)
    collectionRef = collectionRef.where("status", Array.isArray(params?.status) ? "in" : "==", params.status);
  if (params?.brandId) collectionRef = collectionRef.where("brand.id", "==", params.brandId);
  if (params?.retailerId) collectionRef = collectionRef.where("boutique.id", "==", params.retailerId);
  if (params?.repId !== undefined) collectionRef = collectionRef.where("rep.id", "==", params.repId);
  if (params?.term)
    collectionRef = collectionRef.where("searchableValues", "array-contains", params.term.toLowerCase());
  if (params?.isSalesRep !== undefined) collectionRef = collectionRef.where("isSalesRep", "==", params?.isSalesRep);

  return collectionRef.where("type", "==", "User").withConverter<tUserConnection>({
    toFirestore: (data) => data,
    fromFirestore: (snapshot) => validate("tUserConnection")(snapshot.data()),
  });
};

export const queryBrandRepConnection = (
  params?: tQueryBrandRepConnectionsParams,
): firebase.firestore.Query<tRepConnection> => {
  let collectionRef: firebase.firestore.Query = firebase
    .firestore()
    .collection("rep_connections")
    .orderBy("startDate", "desc");

  if (params?.id) collectionRef = collectionRef.where("uuid", "==", params.id);
  if (params?.source) collectionRef = collectionRef.where("connectionSource", "==", params.source);
  if (params?.status)
    collectionRef = collectionRef.where("status", Array.isArray(params?.status) ? "in" : "==", params.status);
  if (params?.brandId) collectionRef = collectionRef.where(`business.id`, "==", params.brandId);
  if (params?.repId !== undefined) collectionRef = collectionRef.where(`rep.id`, "==", params.repId);
  if (params?.term)
    collectionRef = collectionRef.where("searchableValues", "array-contains", params.term.toLowerCase());

  return collectionRef.where("type", "==", "Rep").withConverter<tRepConnection>({
    toFirestore: (data) => data,
    fromFirestore: (snapshot) => validate("tRepConnection")(snapshot.data()),
  });
};

export const queryRepRetailerConnection = (params?: {
  status?: tConnectionStatus | tConnectionStatus[];
  repId: string;
  retailerId?: string;
  term?: string;
}): firebase.firestore.Query<tRepRetailerConnection> => {
  let collectionRef: firebase.firestore.Query = firebase
    .firestore()
    .collection("reps_retailers_connections")
    .orderBy("createdAt", "desc");

  if (params?.status)
    collectionRef = collectionRef.where("status", Array.isArray(params?.status) ? "in" : "==", params.status);
  if (params?.repId) collectionRef = collectionRef.where(`repId`, "==", params.repId);
  if (params?.retailerId) collectionRef = collectionRef.where(`retailerId`, "==", params.retailerId);
  if (params?.term)
    collectionRef = collectionRef.where("searchableValues", "array-contains", params.term.toLowerCase());

  return collectionRef.withConverter<tRepRetailerConnection>({
    toFirestore: (data) => data,
    fromFirestore: (snapshot) => validate("tRepRetailerConnection")(snapshot.data()),
  });
};
